import { formatName } from "@api";
import type { IChallenge } from "@bbj-types/challenge";
import type { ISettingMaster } from "@bbj-types/setting";
import type {
  ISocialOfferWithFavoriteChallenges,
  TSocialOfferCreationOrEdition,
} from "@root/api/services/offers/socialOffer/types";
import type { DefaultValues } from "react-hook-form";
import type { TModalSocialOfferFormResolver } from "./resolver";

export const formatFormToSocialOffer = (
  data: TModalSocialOfferFormResolver,
  isAuthorizedTags: boolean
): TSocialOfferCreationOrEdition => {
  return {
    name: data.name ?? "",
    ...(data.permanent !== "permanent" &&
      data.serviceDates?.startDate && { serviceStartDate: data.serviceDates.startDate }),
    ...(data.permanent !== "permanent" && data.serviceDates?.endDate && { serviceEndDate: data.serviceDates.endDate }),
    tags: isAuthorizedTags
      ? data.tags
      : (data.challenges?.reduce<string[]>((tags, challenge) => {
          if (challenge) {
            tags.push(...challenge.tags);
          }
          return tags;
        }, []) ?? []),
    financingModes: data.financingModes.map((settingValue) => settingValue.value),
    description: data.description,
    beneficiaries: data.beneficiaries,
    registrationModalities: data.registrationModalities,
    url: data.url,
    address: data.address ?? undefined,
    loc: data.address?.loc ?? undefined,
    provider: {
      ...data.provider,
      ...(data.provider.providerId ? { providerId: data.provider.providerId } : { providerId: undefined }),
    },
    perimeter: data.perimeter?.map((place) => place._id),
    ...(data.contact && {
      contact: {
        name: formatName(data.contact),
        comment: data.contact.job ?? "",
        mail: data.contact.email ?? "",
        phone: data.contact.phone ?? "",
      },
    }),
    isRemote: data.isRemote,
  };
};

export const formatSocialOfferToDefaultValues = (
  socialOffer: ISocialOfferWithFavoriteChallenges,
  challenges: IChallenge[],
  financingModes?: ISettingMaster
): DefaultValues<TModalSocialOfferFormResolver> => {
  const [firstName, lastName] = socialOffer.contact?.name?.split(/ +(.*)/) ?? [];
  const permanent = Number(!socialOffer.serviceStartDate && !socialOffer.serviceEndDate);
  return {
    name: socialOffer.name,
    permanent: permanent ? "permanent" : "temporary",
    serviceDates: permanent ? null : { startDate: socialOffer.serviceStartDate, endDate: socialOffer.serviceEndDate },
    challenges: challenges.reduce<IChallenge[]>((challenges, challenge) => {
      if (socialOffer.tags?.some((tag) => challenge.tags.includes(tag))) {
        challenges.push(challenge);
      }
      return challenges;
    }, []),
    financingModes:
      financingModes?.values.filter((settingValue) =>
        socialOffer.financingModes?.some((value) => settingValue.value === value)
      ) ?? [],
    description: socialOffer.description ?? "",
    beneficiaries: socialOffer.beneficiaries ?? "",
    registrationModalities: socialOffer.registrationModalities ?? "",
    url: socialOffer.url ?? "",
    address: socialOffer.address,
    provider: {
      name: socialOffer.provider?.name ?? "",
      providerId: socialOffer.provider?.providerId ?? "",
      website: socialOffer.provider?.website ?? "",
    },
    contact: socialOffer.contact
      ? {
          firstName: firstName ?? "",
          lastName: lastName ?? "",
          job: socialOffer.contact.comment ?? "",
          phone: socialOffer.contact.phone ?? "",
          email: socialOffer.contact.mail ?? "",
        }
      : null,
    documents: [],
    perimeter: socialOffer.perimeter,
    tags: socialOffer.tags,
    isRemote: socialOffer.isRemote,
  };
};
